import { addDays, includesMember, WEEKDAY_SHORT } from '@shh/shared';
import { Agenda, cn, personSoft, personSolid } from '@shh/ui';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { agendaItems, dayLabel } from '../../data/agenda';
import {
  addMonths,
  calendarDay,
  daySpans,
  isoWeek,
  isSpan,
  weekStart,
  type Layers,
} from '../../data/calendar';
import type { E } from '../../data/derive';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { locale } from '../../lib/locale';
import { useSwipe } from '../../lib/useSwipe';
import { useSwipePane } from '../../lib/useSwipePane';
import { dayNumber, dayTitle } from './format';

export interface MonthProps {
  f: FamilyData;
  date: string;
  now: string;
  lens: string;
  layers: Layers;
  people?: string[];
  /** Phone: the month is folded to the chosen week (Mike: it follows the finger). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (date: string) => void;
  onDay: (date: string) => void;
  onWeek: (monday: string) => void;
  onOpen: (id: string, date: string) => void;
  onStep: (dir: 1 | -1, unit: 'week' | 'month') => void;
  /** Phone: the calendar bar above is pinned (pulled in with the month). */
  barPinned?: boolean;
  onBarPinned?: (pinned: boolean) => void;
  /** Phone: height of the pinned calendar bar – the month docks below it. */
  barOffset?: number;
}

/** Weeks (Mondays) of the month around `date`. */
function weeksOf(date: string) {
  const first = `${date.slice(0, 8)}01`;
  const next = addMonths(first, 1);
  const out: string[] = [];
  for (let w = weekStart(first); w < next; w = addDays(w, 7)) out.push(w);
  return out;
}

/** One lane of multi-day bars. */
const BAR = '1.75rem';

const shown = (e: E) =>
  e.virtual !== 'holiday' && e.source !== 'waste' && e.virtual !== 'timetable';

function WeekNumber({ monday, onWeek }: { monday: string; onWeek: (m: string) => void }) {
  const n = isoWeek(monday);
  return (
    <AriaButton
      aria-label={calendarText.openWeek(n)}
      onPress={() => onWeek(monday)}
      className="mx-auto mt-1 grid min-h-6 min-w-6 cursor-pointer place-items-center self-start rounded-control bg-surface-sunken text-xs font-bold text-text-primary tabular-nums outline-none data-hovered:text-text-primary data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
    >
      {n}
    </AriaButton>
  );
}

function Weekdays() {
  return (
    <div className="contents" aria-hidden>
      <span />
      {WEEKDAY_SHORT[locale].map((w) => (
        <span key={w} className="py-1 text-center text-xs font-black text-text-secondary">
          {w}
        </span>
      ))}
    </div>
  );
}

/** Phone: dots per person under each day, the day list below. */
export function MonthPhone(p: MonthProps) {
  const { f, date } = p;
  const weeks = weeksOf(date);
  const sel = Math.max(
    0,
    weeks.findIndex((w) => w <= date && addDays(w, 6) >= date),
  );
  const head = useRef<HTMLDivElement>(null);
  const firstRow = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ head: 0, row: 0 });
  const [drag, setDrag] = useState<number | null>(null);
  // folded by scrolling: no animation, the list must not jump
  const [quiet, setQuiet] = useState(false);
  // scrolling into the future folds the open month with the finger, px by px (Mike, 19.10.:
  // "wenn die Monatsansicht sich synchron mit dem Finger verschieben würde")
  const [fold, setFold] = useState<number | null>(null);
  const folding = useRef({ px: 0, span: 1, timer: 0 });
  // list ↔ month sync (see below)
  const fromList = useRef(false);
  const realign = useRef(false);
  // the first jump when the month opens keeps the calendar bar in view, folded or not
  const firstJump = useRef(true);
  const realignNow = useRef(() => {});
  const quietUntil = useRef(0);
  // where our own jump lands – its scroll event is not the user scrolling
  const jumpY = useRef<number | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const live = useRef({ date, open: p.open, pinned: Boolean(p.barPinned), bar: p.barOffset ?? 0 });
  // height of the docked week strip, so the day's all-day row can dock right below it
  const strip = useRef<HTMLDivElement>(null);
  const [stripH, setStripH] = useState(0);
  useLayoutEffect(() => {
    const el = strip.current;
    if (!el) return;
    const measure = () => setStripH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    setSize({ head: head.current?.offsetHeight ?? 0, row: firstRow.current?.offsetHeight ?? 0 });
  }, [weeks.length, date]);

  const span = Math.max((weeks.length - 1) * size.row, 1);
  const base = p.open ? 1 : 0;
  const progress =
    drag !== null
      ? Math.min(1, Math.max(0, base + drag / span))
      : fold !== null
        ? Math.max(0, 1 - fold / span)
        : base;
  useEffect(() => {
    folding.current.span = span;
  });
  const onPull = (dy: number, done: boolean) => {
    if (!done) return setDrag(dy);
    setDrag(null);
    // a clear pull decides, otherwise the nearer state wins
    const open = Math.abs(dy) > 30 ? dy > 0 : progress > 0.5;
    setOpen(open);
  };
  // pulled open, the calendar bar comes back in – wherever the list is scrolled (Mike, 19.10.)
  const setOpen = (open: boolean) => {
    setQuiet(false);
    p.onOpenChange(open);
    // the month grows for a moment – that is no scrolling of the user
    quietUntil.current = Date.now() + 500;
    if (open && window.scrollY > 0) p.onBarPinned?.(true);
    // once the month is fully open the chosen day lines up below it again
    if (open) window.setTimeout(() => realignNow.current(), 300);
  };
  const turn = (dir: 1 | -1) => p.onStep(dir, p.open ? 'month' : 'week');
  const { ref: gridRef, style: gridStyle } = useSwipePane<HTMLDivElement>(turn, { onPull });
  const handle = useSwipe<HTMLButtonElement>({ onPull });
  const { ref: listRef } = useSwipePane<HTMLDivElement>(turn);

  const measured = size.row > 0;
  const motion =
    drag === null && fold === null && !quiet
      ? 'transition-[height,transform] duration-(--shh-motion-duration-base)'
      : '';

  const cells = (monday: string) =>
    Array.from({ length: 7 }, (_, i) => {
      const d = addDays(monday, i);
      const list = calendarDay(f, d, { layers: p.layers, people: p.people });
      const hol = list.find((e) => e.virtual === 'holiday' && !e.publicHoliday);
      const feiertag = list.find((e) => e.publicHoliday);
      const who = [
        ...new Set(
          list.filter(shown).flatMap((e) => (e.assignees === 'all' ? ['all'] : e.assignees)),
        ),
      ];
      const isSel = d === date;
      const isToday = d === f.today;
      return (
        <AriaButton
          key={d}
          aria-label={`${calendarText.dayEvents(dayTitle(d), list.filter(shown).length)}${feiertag ? `, ${feiertag.title}` : ''}`}
          aria-current={isToday ? 'date' : undefined}
          onPress={() => (isSel ? p.onDay(d) : p.onPick(d))}
          className="relative flex min-h-touch-target cursor-pointer flex-col items-center gap-0.5 pt-1 outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
        >
          <span
            className={cn(
              'grid size-8 place-items-center rounded-avatar font-bold tabular-nums',
              d.slice(0, 7) !== date.slice(0, 7) && 'font-medium text-text-muted',
              feiertag && 'text-feedback-danger',
              isToday && !isSel && 'text-feedback-danger ring-2 ring-feedback-danger',
              isSel && 'bg-text-primary text-surface-canvas',
            )}
          >
            {dayNumber(d)}
          </span>
          <span aria-hidden className="flex h-1.5 gap-0.5">
            {who.slice(0, 4).map((id) => {
              const m = f.members.find((x) => x.id === id);
              return (
                <span
                  key={id}
                  className={cn(
                    'size-1.5 rounded-avatar',
                    m ? personSolid[m.color] : 'bg-accent-secondary',
                  )}
                />
              );
            })}
          </span>
          {hol && (
            <span
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-1 bg-feedback-warning-surface"
            />
          )}
        </AriaButton>
      );
    });

  // the day list under the month: the whole month, opened at the chosen day – scrolling up reaches
  // the 1st (Mike, 19.10.: "dass ich auch bis zum 1. des Monats wieder hochscrollen kann")
  const first = `${date.slice(0, 8)}01`;
  const days: string[] = [];
  for (let d = first; d.slice(0, 7) === first.slice(0, 7); d = addDays(d, 1)) days.push(d);
  /*
   * List and month stay in step (Mike, 19.10.: "egal ob ich in der Liste oder in der Monatsansicht
   * interagiere, die sind immer synchron"): a day picked in the month scrolls the list to it; the
   * day at the top of the scrolled list becomes the picked day; scrolling on into the future
   * folds the month to its week.
   */
  const dockTop = () =>
    (document.querySelector('header')?.getBoundingClientRect().height ?? 0) + (p.barOffset ?? 0);
  const scrollToDay = (day: string) => {
    const el = list.current?.querySelector<HTMLElement>(`[data-day="${day}"]`);
    if (!el) return;
    const line = dockTop() + (strip.current?.offsetHeight ?? 0);
    quietUntil.current = Date.now() + 250;
    // with the month open the calendar bar stays in view while the list jumps
    const keepBar = live.current.open || firstJump.current;
    firstJump.current = false;
    if (keepBar && !live.current.pinned && el.getBoundingClientRect().top > line) {
      realign.current = true;
      p.onBarPinned?.(true);
    }
    const top = Math.round(Math.max(0, el.getBoundingClientRect().top + window.scrollY - line - 8));
    jumpY.current = top;
    window.scrollTo({ top, behavior: 'instant' });
  };
  useLayoutEffect(() => {
    // a day the list itself reported stays where it is
    if (fromList.current) {
      fromList.current = false;
      return;
    }
    scrollToDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the day changes
  }, [date]);
  // after pulling the month open (and pinning the bar) the chosen day lines up again
  useEffect(() => {
    realignNow.current = () => scrollToDay(live.current.date);
  });
  const layoutKey = useRef(`${p.open}|${p.barOffset ?? 0}`);
  useLayoutEffect(() => {
    // only once the month or the bar really changed – not in the same commit that asked for it
    const key = `${p.open}|${p.barOffset ?? 0}`;
    if (key === layoutKey.current) return;
    layoutKey.current = key;
    if (!realign.current) return;
    realign.current = false;
    scrollToDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- after the month or the bar changed
  }, [p.open, p.barOffset]);
  useEffect(() => {
    live.current = { date, open: p.open, pinned: Boolean(p.barPinned), bar: p.barOffset ?? 0 };
  });
  useEffect(() => {
    let last = window.scrollY;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - last;
        last = y;
        if (jumpY.current !== null && Math.abs(y - jumpY.current) < 3) return;
        jumpY.current = null;
        if (Date.now() < quietUntil.current) return;
        const now = live.current;
        const fd = folding.current;
        // into the future: the calendar bar scrolls away …
        if (dy > 6 && now.pinned) p.onBarPinned?.(false);
        // … and the open month folds with the finger: the fold takes the scroll, so the list
        // moves exactly as far as the finger while the month shrinks
        if (now.open && (dy > 0 || fd.px > 0)) {
          const take = dy > 0 ? Math.min(dy, fd.span - fd.px) : -Math.min(-dy, fd.px);
          if (take !== 0) {
            fd.px += take;
            jumpY.current = y - take;
            last = y - take;
            window.scrollTo({ top: y - take, behavior: 'instant' });
            if (fd.px >= fd.span) {
              fd.px = 0;
              setFold(null);
              setQuiet(true);
              p.onOpenChange(false);
            } else setFold(fd.px);
          }
          // let go half way: the nearer state wins
          window.clearTimeout(fd.timer);
          fd.timer = window.setTimeout(() => {
            if (fd.px <= 0) return;
            const shut = fd.px > fd.span / 2;
            fd.px = 0;
            setFold(null);
            if (shut) p.onOpenChange(false);
            window.setTimeout(sync, 300);
          }, 180);
        }
        sync();
      });
    };
    // the day at the top of the list is the picked one
    const sync = () => {
      const now = live.current;
      const header = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
      const line = header + now.bar + (strip.current?.offsetHeight ?? 0) + 1;
      const top = [...(list.current?.querySelectorAll<HTMLElement>('[data-day]') ?? [])].find(
        (s) => s.getBoundingClientRect().bottom > line,
      )?.dataset.day;
      if (top && top !== now.date) {
        fromList.current = true;
        p.onPick(top);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads the live values from refs
  }, []);

  return (
    // no scroll anchoring: the month folding or the bar coming in must not move the list on its own
    <div className="flex flex-col" style={{ overflowAnchor: 'none' }}>
      {/* the month (or the folded week) docks below the app header while the list scrolls under
          it (Mike, 19.10.) */}
      <div
        ref={strip}
        className={cn(drag === null && 'sticky z-15 bg-surface-canvas')}
        style={{ top: `calc(var(--shh-header-h) + ${p.barOffset ?? 0}px)` }}
      >
        <div ref={gridRef} style={gridStyle} className="touch-none">
          <div ref={head} className="grid grid-cols-[1.75rem_repeat(7,minmax(0,1fr))]">
            <Weekdays />
          </div>
          {/* only the weeks are clipped – the weekday row stays clear of the weeks sliding up */}
          <div
            className={cn('overflow-hidden', motion)}
            style={
              measured ? { height: size.row * (1 + (weeks.length - 1) * progress) } : undefined
            }
          >
            <div
              className={cn('flex flex-col', motion)}
              style={
                measured
                  ? { transform: `translateY(${-sel * size.row * (1 - progress)}px)` }
                  : undefined
              }
            >
              {weeks.map((w, i) => (
                <div
                  key={w}
                  ref={i === 0 ? firstRow : undefined}
                  className="grid grid-cols-[1.75rem_repeat(7,minmax(0,1fr))]"
                >
                  <WeekNumber monday={w} onWeek={p.onWeek} />
                  {cells(w)}
                </div>
              ))}
            </div>
          </div>
        </div>
        <AriaButton
          ref={handle}
          aria-label={p.open ? calendarText.monthClose : calendarText.monthOpen}
          aria-expanded={p.open}
          onPress={() => setOpen(!p.open)}
          className="flex min-h-touch-min w-full touch-none cursor-ns-resize items-center justify-center outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
        >
          <span className="h-1.5 w-10 rounded-chip bg-border-default" />
        </AriaButton>
      </div>
      <div
        ref={(el) => {
          list.current = el;
          listRef.current = el;
        }}
        // room below the last day, so every day of the month can reach the top of the list
        style={{ paddingBottom: '60vh' }}
        className="flex touch-pan-y flex-col gap-gap-md"
      >
        {days.map((d) => {
          // only today steps back what is over – earlier days of the month stay easy to read
          const items = agendaItems(f, d, p.now, p.lens, () => null, {
            list: calendarDay(f, d, { layers: p.layers, people: p.people }),
          }).map((x) => ({
            ...x,
            dimmed: false,
            state: d === f.today ? x.state : ('later' as const),
          }));
          // empty days stay out – except the chosen day and today
          const show =
            d === date || d === f.today || items.some((x) => x.start || x.pinTone === undefined);
          if (!show) return null;
          const info = dayLabel(f, d, true);
          return (
            <section
              key={d}
              data-day={d}
              aria-label={dayTitle(d)}
              className="flex flex-col gap-gap-sm"
            >
              <h3 className="font-display text-lg font-black">
                {info.label}, {info.date}
              </h3>
              {items.length ? (
                <Agenda
                  items={items}
                  pinAllDay
                  // all-day items dock too – below the week strip when it is docked (Mike, 19.10.)
                  pinTop={`calc(var(--shh-header-h) + ${(p.barOffset ?? 0) + stripH}px)`}
                  nowLabel={d === f.today ? p.now : undefined}
                  onItemPress={(id) => p.onOpen(id, d)}
                />
              ) : (
                <p className="text-sm text-text-muted">{calendarText.noEvents}</p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** Tablet and computer: the month as a wall calendar with titles in the days. */
export function MonthWide(p: MonthProps) {
  const { f, date } = p;
  const weeks = weeksOf(date);
  const { ref, style } = useSwipePane<HTMLDivElement>((dir) => p.onStep(dir, 'month'));
  return (
    <div
      ref={ref}
      style={style}
      className="touch-pan-y overflow-hidden rounded-card bg-surface-raised"
    >
      <div className="grid grid-cols-[2.5rem_repeat(7,minmax(0,1fr))]">
        <Weekdays />
        {weeks.map((w) => {
          const weekDays = Array.from({ length: 7 }, (_, i) => addDays(w, i));
          const lists = weekDays.map((d) =>
            calendarDay(f, d, { layers: p.layers, people: p.people }),
          );
          // events over several days as one bar across the week (Mike, 19.10.)
          const bars = daySpans(weekDays, lists, shown);
          const lanes = bars.reduce((n, b) => Math.max(n, b.lane + 1), 0);
          return (
            <div key={w} className="contents">
              <WeekNumber monday={w} onWeek={p.onWeek} />
              {weekDays.map((d, i) => {
                const list = lists[i]!;
                const events = list.filter((e) => shown(e) && !isSpan(e));
                const hol = list.find((e) => e.virtual === 'holiday' && !e.publicHoliday);
                const feiertag = list.find((e) => e.publicHoliday);
                const isSel = d === date;
                const isToday = d === f.today;
                return (
                  <div
                    key={d}
                    className={cn(
                      'relative flex min-h-28 min-w-0 flex-col gap-0.5 border-l border-t border-border-subtle p-1',
                      d.slice(0, 7) !== date.slice(0, 7) && 'bg-surface-sunken',
                      isToday && 'bg-feedback-danger-surface',
                    )}
                  >
                    <div className="flex h-7 min-w-0 items-center gap-1">
                      <AriaButton
                        aria-label={calendarText.dayEvents(dayTitle(d), events.length)}
                        aria-current={isToday ? 'date' : undefined}
                        onPress={() => (isSel ? p.onDay(d) : p.onPick(d))}
                        className={cn(
                          'grid size-7 shrink-0 cursor-pointer place-items-center rounded-avatar text-sm font-bold tabular-nums outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                          feiertag && 'text-feedback-danger',
                          isToday && !isSel && 'text-feedback-danger ring-2 ring-feedback-danger',
                          isSel && 'bg-text-primary text-surface-canvas',
                        )}
                      >
                        {dayNumber(d)}
                      </AriaButton>
                      {feiertag && (
                        <span className="truncate text-xs font-bold text-feedback-danger">
                          {feiertag.title}
                        </span>
                      )}
                    </div>
                    {/* room for the bars of this week, then the day's own events */}
                    {lanes > 0 && (
                      <span aria-hidden style={{ height: `calc(${lanes} * ${BAR})` }} />
                    )}
                    {bars
                      .filter((b) => b.from === i)
                      .map((b) => {
                        const m =
                          b.e.assignees === 'all'
                            ? undefined
                            : f.members.find((x) => includesMember(b.e.assignees, x.id));
                        return (
                          <AriaButton
                            key={b.e.id}
                            onPress={() => p.onOpen(b.e.id, d)}
                            className={cn(
                              'absolute left-1 z-10 flex h-6 cursor-pointer items-center gap-1 truncate rounded-control px-1.5 text-left text-xs font-bold outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                              m ? personSoft[m.color] : 'bg-accent-secondary-soft',
                              b.cutStart && 'rounded-l-none',
                              b.cutEnd && 'rounded-r-none',
                            )}
                            style={{
                              top: `calc(2.25rem + ${b.lane} * ${BAR})`,
                              width: `calc(${b.len} * 100% + ${b.len - 1}px - 0.5rem)`,
                            }}
                          >
                            {b.e.title}
                          </AriaButton>
                        );
                      })}
                    {events.slice(0, 3).map((e) => {
                      const m =
                        e.assignees === 'all'
                          ? undefined
                          : f.members.find((x) => includesMember(e.assignees, x.id));
                      return (
                        <AriaButton
                          key={e.id}
                          onPress={() => p.onOpen(e.id, d)}
                          className="flex min-h-6 min-w-0 cursor-pointer items-center gap-1 rounded-control px-0.5 text-left text-xs font-semibold outline-none data-hovered:bg-action-quiet-hover data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
                        >
                          <span
                            aria-hidden
                            className={cn(
                              'size-2 shrink-0 rounded-avatar',
                              m ? personSolid[m.color] : 'bg-accent-secondary',
                            )}
                          />
                          <span className="truncate">
                            {e.start ? `${e.start} ` : ''}
                            {e.title}
                          </span>
                        </AriaButton>
                      );
                    })}
                    {events.length > 3 && (
                      <AriaButton
                        onPress={() => p.onDay(d)}
                        className="min-h-6 cursor-pointer px-0.5 text-left text-xs font-bold text-text-primary outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
                      >
                        {calendarText.more(events.length - 3)}
                      </AriaButton>
                    )}
                    {hol && (
                      <span
                        aria-hidden
                        title={hol.title}
                        className="absolute inset-x-0 bottom-0 h-1 bg-feedback-warning-surface"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
